function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
    // document.body.classList.add(`overflow-hidden`);
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    const isFirstLevelMenuItem = detailsElement.parentNode.classList.contains(
      "menu-drawer__menu-lvl0"
    );

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();

      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        if (isFirstLevelMenuItem) {
          const openLevel0Details = this.mainDetailsToggle.querySelectorAll(
            ".menu-drawer__menu-lvl0 > details[open]"
          );

          openLevel0Details.forEach((detail) => {
            if (detail !== detailsElement) {
              detail.classList.remove("menu-opening");
              detail.removeAttribute("open");
              detail
                .querySelector("summary")
                ?.setAttribute("aria-expanded", false);
            }
          });
        }
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(`overflow-hidden`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    document.body.classList.remove(`overflow-hidden`);
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");

      const firstLevelItem = this.mainDetailsToggle.querySelector("details");
      if (firstLevelItem) {
        firstLevelItem.classList.add("menu-opening");
        firstLevelItem.setAttribute("open", true);
        firstLevelItem
          .querySelector("summary")
          ?.setAttribute("aria-expanded", true);
      }
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    /* document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`); */
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    /* const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this)); */
    this.loadContent(false);
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (deferredElement.nodeName == "VIDEO") {
        deferredElement.addEventListener("loadeddata", () => {
          this.classList.remove("loading");
          deferredElement.play();
        });

        deferredElement.setAttribute("autoplay", "true");
        deferredElement.muted = true;
        // force autoplay for safari
        deferredElement.play();
      } else if (deferredElement.nodeName === "IFRAME") {
        deferredElement.addEventListener("load", () => {
          this.classList.remove("loading");
        });
      }

      if (focus) deferredElement.focus();
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.bulletsContainer = this.querySelector(".slider-bullets");

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    if (this.bulletsContainer) {
      if (this.sliderItemsToShow.length > 4 || window.innerWidth < 768) {
        this.createBullets();
      }
    }

    this.update();
  }

  createBullets() {
    this.bulletsContainer.innerHTML = "";
    for (let i = 0; i < this.totalPages; i++) {
      const bullet = document.createElement("span");
      bullet.classList.add("slider-bullet");
      bullet.addEventListener("click", () => this.goToPage(i + 1));
      this.bulletsContainer.appendChild(bullet);
    }

    this.highlightBullet(1);
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;
    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
    if (this.bulletsContainer) {
      this.highlightBullet(this.currentPage);
    }
  }

  highlightBullet(pageNumber) {
    const bullets = this.bulletsContainer.querySelectorAll(".slider-bullet");
    bullets.forEach((bullet, index) => {
      bullet.classList.toggle("active", index + 1 === pageNumber);
    });
  }

  goToPage(pageNumber) {
    const targetScrollPosition = (pageNumber - 1) * this.sliderItemOffset;
    this.slider.scrollTo({
      left: targetScrollPosition,
      behavior: "smooth",
    });
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

function updateVariantAdditionalInfo() {
  let url = window.location.href;
  let variantId = url.split("variant=")[1];

  if (variantId && variantMetafieldInfo[variantId] !== undefined) {
    let metafields = variantMetafieldInfo[variantId];
    if (metafields) {
      document.getElementById("meta1").textContent = metafields.meta1 || "";
      document.getElementById("meta2").textContent = metafields.meta2 || "";
      document.getElementById("meta3").textContent = metafields.meta3 || "";
      document.getElementById("meta4").textContent = metafields.meta4 || "";
      document.getElementById("meta5").textContent = metafields.meta5 || "";
      document.getElementById("meta6").textContent = metafields.meta6 || "";
      document.getElementById("meta7").textContent = metafields.meta7 || "";
      document.getElementById("meta8").textContent = metafields.meta8 || "";
      document.getElementById("meta9").textContent = metafields.meta9 || "";
      document.getElementById("meta10").textContent = metafields.meta10 || "";
      document.getElementById("meta11").textContent = metafields.meta11 || "";
      document.getElementById("meta12").textContent = metafields.meta12 || "";
      document.getElementById("meta13").textContent = metafields.meta13 || "";
      document.getElementById("meta14").textContent = metafields.meta14 || "";
      document.getElementById("meta15").textContent = metafields.meta15 || "";
      document.getElementById("meta16").textContent = metafields.meta16 || "";
      document.getElementById("meta17").textContent = metafields.meta17 || "";
      document.getElementById("meta18").textContent = metafields.meta18 || "";
    }
  }
  updateMetafieldDisplay();
}

// Draggable
function updateMetafieldDisplay() {
  var metafieldFields = document.querySelectorAll("span.metafield-field");
  metafieldFields.forEach(function (field) {
    if (field.textContent.trim() === "") {
      field.parentElement.style.display = "none";
    } else {
      field.parentElement.style.display = "flex";
    }
  });
}

document.addEventListener("DOMContentLoaded", function () {
  function updateMetafieldDisplay() {
    var metafieldFields = document.querySelectorAll("span.metafield-field");
    metafieldFields.forEach(function (field) {
      if (field.textContent.trim() === "") {
        field.parentElement.style.display = "none";
      } else {
        field.parentElement.style.display = "flex";
      }
    });
  }
  updateMetafieldDisplay();
});

document.addEventListener("DOMContentLoaded", function () {
  function initializeDrag(sliderContainer) {
    let isDown = false;
    let startX;
    let scrollLeft;

    sliderContainer.addEventListener("mousedown", function (e) {
      isDown = true;
      startX = e.pageX - sliderContainer.offsetLeft;
      scrollLeft = sliderContainer.scrollLeft;
      e.preventDefault();
    });

    sliderContainer.addEventListener("mouseleave", function () {
      isDown = false;
    });

    sliderContainer.addEventListener("mouseup", function () {
      isDown = false;
    });

    sliderContainer.addEventListener("mousemove", function (e) {
      if (!isDown) return;
      e.preventDefault();
      let x = e.pageX - sliderContainer.offsetLeft;
      let walk = x - startX;
      sliderContainer.scrollLeft = scrollLeft - walk;
    });
  }

  let sliderContainers = document.querySelectorAll(".draggable");
  sliderContainers.forEach(initializeDrag);

  document.body.addEventListener("mouseover", function (e) {
    if (e.target.matches(".draggable") && !e.target.dragInitialized) {
      initializeDrag(e.target);
      e.target.dragInitialized = true;
    }
  });
});

function initializeImageNavigation() {
  document.querySelectorAll(".card-wrapper").forEach((cardWrapper) => {
    const mediaContainers = cardWrapper.querySelectorAll(".card__inner .media");
    const imagePickers = cardWrapper.querySelectorAll(".image_picker");
    const prevArrow = cardWrapper.querySelector(".prev-arrow");
    const nextArrow = cardWrapper.querySelector(".next-arrow");

    mediaContainers.forEach((mediaContainer) => {
      const images = mediaContainer.querySelectorAll("img");

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const visibleImageIndex = Array.from(images).findIndex(
              (img) => img === entry.target
            );
            if (entry.isIntersecting) {
              images.forEach((img) => img.classList.remove("selected"));
              imagePickers.forEach((picker) =>
                picker.classList.remove("selected")
              );

              if (visibleImageIndex !== -1) {
                images[visibleImageIndex].classList.add("selected");
                const correspondingPicker = cardWrapper.querySelector(
                  `.image_picker[data-index="${visibleImageIndex}"]`
                );
                if (correspondingPicker)
                  correspondingPicker.classList.add("selected");
              }
            }
          });
        },
        {
          root: mediaContainer,
          threshold: 0.5,
        }
      );

      imagePickers.forEach((picker) => {
        picker.addEventListener("click", function (event) {
          const index = parseInt(
            event.currentTarget.getAttribute("data-index"),
            10
          );
          const mediaContainer = cardWrapper.querySelector(".scrollable-media");
          const targetImage = mediaContainer.querySelectorAll("img")[index];

          if (targetImage) {
            mediaContainer
              .querySelectorAll("img")
              .forEach((img) => img.classList.remove("selected"));
            imagePickers.forEach((picker) =>
              picker.classList.remove("selected")
            );

            event.currentTarget.classList.add("selected");
            targetImage.classList.add("selected");

            const scrollPosition = targetImage.offsetLeft;
            mediaContainer.scrollTo({
              left: scrollPosition,
              behavior: "smooth",
            });
          }
        });
      });

      function navigateToImage(direction) {
        let currentIndex = Array.from(images).findIndex((img) =>
          img.classList.contains("selected")
        );
        currentIndex += direction;
        if (currentIndex < 0) currentIndex = images.length - 1;
        if (currentIndex >= images.length) currentIndex = 0;

        const targetImage = images[currentIndex];
        const targetPicker = cardWrapper.querySelector(
          `.image_picker[data-index="${currentIndex}"]`
        );

        if (targetImage && targetPicker) {
          images.forEach((img) => img.classList.remove("selected"));
          imagePickers.forEach((picker) => picker.classList.remove("selected"));

          targetImage.classList.add("selected");
          targetPicker.classList.add("selected");

          const scrollPosition = targetImage.offsetLeft;
          mediaContainer.scrollTo({
            left: scrollPosition,
            behavior: "smooth",
          });
        }
      }

      if (prevArrow) {
        prevArrow.addEventListener("click", () => navigateToImage(-1));
      }
      if (nextArrow) {
        nextArrow.addEventListener("click", () => navigateToImage(1));
      }

      images.forEach((img) => observer.observe(img));
    });
  });
}

/* QUICK ADD */
function initializeVariantButtons(container) {
  const variantButtons = container.querySelectorAll(".variant-button");
  const selectedVariantInput = container.querySelector("#selectedVariantId");

  variantButtons.forEach((button) => {
    button.addEventListener("click", (e) => {
      let quickAddSheet = document.getElementById("quickAddSheet");
      const selectedOption = button.getAttribute("data-variant-id");
      if (button.disabled) {
        e.preventDefault();
        alert("This variant is unavailable");
        return;
      }

      const loadingSpinner = button.querySelector(".loading__spinner");
      if (loadingSpinner) {
        loadingSpinner.classList.remove("hidden");
      }

      if (selectedVariantInput) {
        selectedVariantInput.value = selectedOption;
        if (quickAddSheet) {
          quickAddSheet.classList.add("hidden");
        }
        document.body.classList.remove("overflow-hidden");
      }
    });
  });
  hideOnAdd();
}

function hideOnAdd() {
  const addButton = document.querySelectorAll(
    "#quickAddSheet .op__container .quick-add__submit"
  );

  addButton.forEach((button) => {
    if (button) {
      button.addEventListener("click", () => {
        const quickAddSheet = document.getElementById("quickAddSheet");
        if (quickAddSheet) {
          quickAddSheet.classList.add("hidden");
          document.body.classList.remove("overflow-hidden");
        } else {
          console.warn("quickAddSheet not found");
        }
      });
    } else {
      console.warn("No buttons found");
    }
  });

  if (addButton.length === 0) {
    console.warn("No buttons were added");
  }
}

function updateQuickAddVariants(quickAddContainer, variantData) {
  const variantButtonsContainer =
    quickAddContainer.querySelector(".variant-radios");

  if (!variantButtonsContainer) {
    console.error("cannot find container");
    return;
  }

  variantButtonsContainer.innerHTML = "";

  variantData.forEach((variant, index) => {
    const button = document.createElement("button");
    button.classList.add("variant-button");
    button.setAttribute("data-variant-id", variant.id);
    button.setAttribute("data-index", index + 1);
    button.setAttribute("type", "submit");

    if (!variant.available) {
      button.classList.add("disabled");
      button.setAttribute("aria-disabled", "true");
    } else {
      button.setAttribute("aria-disabled", "false");
    }

    const span = document.createElement("span");

    if (variantData.length > 1) {
      span.textContent = variant.title != "UNICO" ? variant.title : "";
    } else {
      span.classList.add("svg-wrapper");
      span.innerHTML = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 0.5C4.5 0.223858 4.72386 0 5 0C5.27614 0 5.5 0.223858 5.5 0.5V4.5H9.5C9.77614 4.5 10 4.72386 10 5C10 5.27614 9.77614 5.5 9.5 5.5H5.5V9.5C5.5 9.77614 5.27614 10 5 10C4.72386 10 4.5 9.77614 4.5 9.5V5.5H0.5C0.223858 5.5 0 5.27614 0 5C0 4.72386 0.223858 4.5 0.5 4.5H4.5V0.5Z" fill="currentColor"/>
      </svg>`;
    }

    button.appendChild(span);

    const spinnerDiv = document.createElement("div");
    spinnerDiv.classList.add(
      "loading__spinner",
      "hidden",
      "small-hide",
      "large-up-hide"
    );
    spinnerDiv.setAttribute("hidden", "");
    spinnerDiv.innerHTML = `<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
    </svg>`;

    button.appendChild(spinnerDiv);

    variantButtonsContainer.appendChild(button);
  });

  initializeVariantButtons(quickAddContainer);
}

function variantImages() {
  document.querySelectorAll(".product-card-wrapper").forEach((cardWrapper) => {
    const principalMediaContainer = cardWrapper.querySelector(
      ".card__inner .media"
    );
    const variationSwatches = cardWrapper.querySelectorAll(
      ".product_variant__swatch"
    );
    if (principalMediaContainer) {
      const originalImage = principalMediaContainer.innerHTML;

      let isImageChanged = false;

      function resetImage() {
        if (isImageChanged) {
          principalMediaContainer.innerHTML = originalImage;
          isImageChanged = false;
        }
      }

      variationSwatches.forEach((swatch) => {
        swatch.addEventListener("click", function (event) {
          event.stopPropagation();
          principalMediaContainer.innerHTML = "";

          const newImage = swatch.querySelector(".image-cards img");
          if (newImage) {
            const clonedImg = newImage.cloneNode(true);
            principalMediaContainer.appendChild(clonedImg);
            isImageChanged = true;
          }

          const container = cardWrapper.querySelector(".op__container");
          if (container) {
            const scriptData = swatch.querySelector(".product-list-json");
            const variantData = JSON.parse(scriptData.textContent);

            updateQuickAddVariants(container, variantData);
          }
        });

        cardWrapper.addEventListener("click", function (event) {
          if (
            !event.target.closest(".product_variant__swatch") &&
            !event.target.closest(".quick-add")
          ) {
            resetImage();
          }
        });
      });
    }
  });
}

function handleMobileSheet(button) {
  const parentContainer = button.closest(".quick-add__container");
  const dropdown = parentContainer.querySelector(
    ".quick-add__dropdown"
  ).innerHTML;

  let quickAddSheet = document.getElementById("quickAddSheet");
  if (!quickAddSheet) {
    quickAddSheet = document.createElement("div");
    quickAddSheet.id = "quickAddSheet";
    quickAddSheet.className = "quick-add__sheet hidden";

    quickAddSheet.innerHTML = `
        <div class="quick-add__sheet-content">
          <span class="close__bar medium-hide large-up-hide close-mobile-btn"></span>
          <div class="quick-add__container"></div>
        </div>
      `;

    document.body.appendChild(quickAddSheet);

    quickAddSheet.addEventListener("click", (event) => {
      if (event.target === quickAddSheet) {
        quickAddSheet.classList.add("hidden");
        document.body.classList.remove("overflow-hidden");
      }
    });

    const detailsCloseButton = quickAddSheet.querySelector(".close-mobile-btn");

    if (detailsCloseButton) {
      detailsCloseButton.addEventListener("click", () => {
        quickAddSheet.classList.add("hidden");
        document.body.classList.remove("overflow-hidden");
      });
    }
  }

  const sheetContainer = quickAddSheet.querySelector(".quick-add__container");
  sheetContainer.innerHTML = dropdown;
  initializeVariantButtons(sheetContainer);

  quickAddSheet.classList.remove("hidden");
  document.body.classList.add("overflow-hidden");
}

function handleDesktopOverlay(button) {
  const parentContainer = button.closest(".quick-add__container");
  const dropdown = parentContainer.querySelector(".quick-add__dropdown");

  // Toggle the active class
  const isActive = dropdown.classList.contains("active");
  dropdown.classList.toggle("active", !isActive);

  // Update the aria-expanded attribute
  button.setAttribute("aria-expanded", !isActive);
}

function handleQuickAdd() {
  const quickAddButtons = document.querySelectorAll(".quick-add__submit");

  quickAddButtons.forEach((button) => {
    button.addEventListener("click", (event) => {
      const screenWidth = window.innerWidth;
      const isMobileOrTablet = screenWidth <= 1024; // inclui mobile + tablets

      if (isMobileOrTablet) {
        handleMobileSheet(button); // mostra o botão, abre painel
      } else {
        handleDesktopOverlay(button); // ativa hover ou dropdown
      }
    });
  });

  // Fechar dropdowns no desktop
  document.addEventListener("click", (event) => {
    const clickedButton = event.target.closest(".quick-add__submit");
    const screenWidth = window.innerWidth;

    if (screenWidth > 1024) {
      document.querySelectorAll(".quick-add__dropdown.active").forEach((dropdown) => {
        const parentContainer = dropdown.closest(".quick-add__container");
        const button = parentContainer.querySelector(".quick-add__submit");

        if (clickedButton !== button) {
          dropdown.classList.remove("active");
          button.setAttribute("aria-expanded", "false");
        }
      });
    }
  });
}


function viewGrid() {
  let viewGrid = document.querySelectorAll(".collection-view .view-two");
  let viewRow = document.querySelectorAll(".collection-view .view-one");
  let grid = document.querySelector(".collection #productGrid");

  if (viewGrid) {
    viewGrid.forEach((viewGrid) => {
      viewGrid.addEventListener("click", () => {
        event.preventDefault();
        viewGrid.classList.add("active");
        viewRow.forEach((viewRow) => {
          viewRow.classList.remove("active");
        });
        grid.classList.remove("one-view");
        grid.classList.add("grid--4-col-desktop");
        grid.classList.remove("grid--2-col-desktop");
      });
    });

    viewRow.forEach((viewRow) => {
      viewRow.addEventListener("click", () => {
        event.preventDefault();
        viewRow.classList.add("active");
        viewGrid.forEach((viewGrid) => {
          viewGrid.classList.remove("active");
        });
        grid.classList.add("grid--2-col-desktop");
        grid.classList.remove("grid--4-col-desktop");
      });
    });
  }
}

document.addEventListener("DOMContentLoaded", function () {
  variantImages();
  viewGrid();
  initializeImageNavigation();
  handleQuickAdd();
  document.querySelectorAll(".quick-add__container").forEach((container) => {
    initializeVariantButtons(container);
  });
  hideOnAdd();
});

document.addEventListener("facetsUpdated", function () {
  variantImages();
  viewGrid();
  initializeImageNavigation();
  handleQuickAdd();
  document.querySelectorAll(".quick-add__container").forEach((container) => {
    initializeVariantButtons(container);
  });
  hideOnAdd();
});

function setupProductGridObserver() {
  const productGrid = document.getElementById("productGrid");
  if (!productGrid) return;
  const observer = new MutationObserver((mutations) => {
    const hasNewContent = mutations.some(
      (mutation) =>
        (mutation.type === "childList" && mutation.addedNodes.length > 0) ||
        mutation.type === "characterData"
    );

    if (hasNewContent) {
      console.log("new");
      requestAnimationFrame(() => {
        variantImages();
        handleQuickAdd();
        document
          .querySelectorAll(".quick-add__container")
          .forEach((container) => {
            initializeVariantButtons(container);
          });
        hideOnAdd();
      });
    }
  });

  observer.observe(productGrid, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  return observer;
}

document.addEventListener("DOMContentLoaded", () => {
  const gridObserver = setupProductGridObserver();

  // Clean up the observer when navigating away
  window.addEventListener("beforeunload", () => {
    if (gridObserver) gridObserver.disconnect();
  });
});

document.addEventListener("facetsUpdated", () => {
  const gridObserver = setupProductGridObserver();

  window.addEventListener("beforeunload", () => {
    if (gridObserver) gridObserver.disconnect();
  });
});



var linkCreated = document.createElement('a');
linkCreated.style = "text-decoration: none"
var containerDiv = document.querySelectorAll('.multi-block-body--item ')
var linkBlock = document.querySelectorAll('.section-card .menu-link__md')
var lastElement;
linkBlock.forEach((e)=>{
  linkCreated.href = e.href
  containerDiv.forEach((last)=>{
    lastElement = last.lastElementChild;
  })
})
linkCreated.appendChild(lastElement.cloneNode(true))
lastElement.replaceWith(linkCreated)




